import { Injectable } from '@angular/core';
import { TranslateService }    from '@ngx-translate/core';
import { NgbModal }            from '@ng-bootstrap/ng-bootstrap';
import { UIRouter }            from '@uirouter/core';

import { ApiService }                        from '../api/api.service';
import { BillerService }                     from '../biller/biller.service';
import { CompanyService }                    from '../companies/company.service';
import { ConfirmService }                    from '../messages/confirm/confirm.service';
import { DateParserService }                 from '../core/services/date-parser.service';
import { InvoiceModel }                      from '../invoices/models/invoice';
import { MessengerService }                  from '../messages/messenger/messenger.service';
import { TableQueryParameters }              from '../table/types/table-query-parameters';
import { TableService }                      from '../table/table.service';
import { VendorModel }                       from '../vendors/models/vendor';
import { VendorPaymentEmailRemittanceComponent } from './vendor-payment.email-remittance.component';
import { VendorPaymentModel }                from './models/vendor-payment';
import { VendorPaymentItemModel }            from './models/vendor-payment.item';
import { VendorPaymentsApiModel }            from './models/vendor-payment.api';

import { InvoicesForBillerPaymentComponent } from '../invoices/invoices.for-biller-payment.component';
import { VendorInvoicesForPaymentComponent } from '../vendor-invoices/vendor-invoices.for-payment.component';

import DynamicColorRowHtml      from '../grids/templates/row.dynamic-color.html';
import EditableCurrencyCellHtml from '../grids/templates/cell.currency.editable.html';
import RemoveRowCellHtml        from '../grids/templates/cell.remove-row.html';

@Injectable({ providedIn: 'root' })
export class VendorPaymentsService implements VendorPaymentsApiModel {
  constructor (
    private apiService      : ApiService,
    private billerApi       : BillerService,
    private companyService  : CompanyService,
    private confirmService  : ConfirmService,
    private dateParsing     : DateParserService,
    private messengerApi    : MessengerService,
    private ngbModal        : NgbModal,
    private router          : UIRouter,
    private tableService    : TableService,
    private translateService: TranslateService
  ) {}

  addBillerCredit ( payment : VendorPaymentModel ) : void {
    Promise.resolve(true)
    .then(() => {
      const unsavedItems = payment.vendor_payment_items_attributes.filter(item => item.vendor_invoice_id && !item.id);

      return unsavedItems.length
        ? this.confirmService.generic(this.translateService.instant('JS_SPACE.CONFIRM.LOSS_OF_UNSAVED_INFORMATION'))
          .then(() => this.save(payment))
          .then(payment => {
            this.router.stateService.go('app/vendor-payments', {
              vendor_id: payment.vendor_id,
              id       : payment.id
            }, {
              location: 'replace'
            });

            return Promise.resolve(true);
          })
        : Promise.resolve(true);
    })
    .then(() => this.billerApi.openLookup(payment.vendor_id, true));
  }

  addInvoices ( payment : VendorPaymentModel ) : Promise<VendorPaymentModel> {
    const tableCacheKey = 'vendorInvoicesForPayment';

    return this.apiService.get('/vendor', payment.vendor_id)
    .then(( vendor : VendorModel ) => {
      return vendor.biller
        ? this.apiService.get('/vendors/biller_list', [ payment.vendor_id, 0, '*', 'P', 'I', '*', '*', '*', 'company_name', 'desc'])
          .then(( billers: Array<any> ) => {
            const modal = this.ngbModal.open(InvoicesForBillerPaymentComponent, { windowClass: 'x-lg-modal flip-container' });
            modal.componentInstance.billers  = billers;
            modal.componentInstance.isVendor = true;
            return modal.result;
          })
        : this.getInvoicesForPayment(vendor, (() => {
            const cache = this.tableService.getCacheForTable(tableCacheKey);
            return { direction: cache.sorting.direction, field: cache.sorting.field };
          })())
          .then(( invoices : Array<any> ) => {
            const modal = this.ngbModal.open(VendorInvoicesForPaymentComponent, { windowClass: 'x-lg-modal' });
            modal.componentInstance.invoices = invoices;
            modal.componentInstance.settings = { tableCacheKey };
            modal.componentInstance.vendor   = vendor;
            return modal.result;
          });
    })
    .then(( invoices : Array<any> ) => {
      const items : Array<VendorPaymentItemModel> = [];

      invoices.forEach(invoice => {
        let item : VendorPaymentItemModel = {
          acquired_type             : '',
          id                        : '',
          stock_number              : '',
          vendor_payment_id         : payment.id,
          vendor_invoice_id         : invoice.id,
          vendor_id                 : invoice.vendor_id,
          vendor_name               : invoice.vendor_name,
          vendor_invoice_number     : invoice.vendor_invoice_number,
          vendor_invoice_post_date  : invoice.post_date,
          vendor_invoice_total      : invoice.total,
          amount                    : invoice.balance_due,
          vendor_invoice_balance_due: invoice.balance_due,
          untouched                 : false
        };

        item = this.prepPaymentItem(payment, item);

        items.push(item);
      });

      payment.vendor_payment_items_attributes = payment.vendor_payment_items_attributes.filter(item => item.vendor_invoice_id);
      payment.vendor_payment_items_attributes = payment.vendor_payment_items_attributes.concat(items);

      return payment;
    });
  }

  addItems ( payment : VendorPaymentModel ) : Promise<VendorPaymentModel> {


    return this.apiService.get('/vendor', payment.vendor_id)
    .then(( vendor : VendorModel ) => this.addInvoices(payment));


  }

  delete ( payment : VendorPaymentModel ) : Promise<boolean> {


    return this.confirmService.generic(this.translateService.instant('JS_SPACE.CONFIRM.DELETE_PAYMENT'))
    .then(() => this.apiService.delete('/vendor_payment', payment.id));


  }

  deleteItems ( payment : VendorPaymentModel ) : Promise<VendorPaymentModel> {
    payment = JSON.parse(JSON.stringify(payment));

    return payment.itemsToDelete.length && payment.id
      ? this.apiService.patch('/vendor_payment', {
        id                             : payment.id,
        amount                         : payment.amount,
        vendor_payment_items_attributes: payment.itemsToDelete }, 'vendor_payment')
      .then(( payment : VendorPaymentModel ) => {
        payment.itemsToDelete = [];

        return payment;
      })
      : Promise.resolve(payment);
  }

  email ( vendor : VendorModel, payment : VendorPaymentModel ) : Promise<VendorPaymentModel> {
    return this.companyService.getDefaultMessages()
    .then(companyMessages => this.messengerApi.email({
      email          : vendor.email,
      id             : vendor.id,
      message        : companyMessages.supplier_email_message,
      send_remittance: true,
      subject        : this.translateService.instant('JS_SPACE.EMAIL.VARIABLE_SUBJECT', {
        name: this.companyService.company.company_name,
        num : payment.vendor_payment_number ? (' number ' + payment.vendor_payment_number) : '',
        type: 'Payment'
      }),
      vendor_payment_attachment_id: '',
      vendor_payment_id           : payment.id
    }, {
      bodyParamName: 'vendor',
      factory      : VendorPaymentEmailRemittanceComponent,
      size         : 'lg',
      url          : '/vendor_payment/email_remittance'
    }));
  }

  get ( vendor_id : string, id : string ) : Promise<VendorPaymentModel> {
    return Promise.resolve()
    .then(() => ( vendor_id && id ) ? this.apiService.get('/vendor_payment', id) : Promise.resolve({}))
    .then(( payment : VendorPaymentModel ) => this.prepPayment(vendor_id, payment));
  }

  getGridSettings ( vendor : VendorModel, editable : boolean ) : any {
    return {
      cellEditableCondition  : 'editable',
      data                   : 'payment.vendor_payment_items_attributes',
      enableCellEditOnFocus  : true,
      enableCellSelection    : true,
      enableMultiSelect      : false,
      enableRowSelection     : false,
      rowHeight              : 43,
      rowTemplate            : DynamicColorRowHtml,
      virtualizationThreshold: 9999,
      columnDefs             : [
        {
          cellTemplate  : RemoveRowCellHtml,
          disabled      : !editable,
          enableCellEdit: false,
          width         : '43px'
        },
        {
          cellClass     : 'Uppercase ReadOnly',
          displayName   : this.translateService.instant('GENERAL_SPACE.FIELD.REFERENCE'),
          enableCellEdit: false,
          field         : 'vendor_invoice_number'
        },
        {
          cellClass     : 'Capitalize ReadOnly',
          cellFilter    : 'date:"dd/MM/yyyy"',
          displayName   : this.translateService.instant('GENERAL_SPACE.FIELD.POST_DATE'),
          enableCellEdit: false,
          field         : 'vendor_invoice_post_date',
          width         : '150px'
        },
        {
          cellClass     : 'Uppercase ReadOnly',
          displayName   : this.translateService.instant('VENDOR_SPACE.SUPPLIER'),
          enableCellEdit: false,
          field         : 'vendor_name'
        },
        {
          cellClass     : 'align-right ReadOnly',
          cellFilter    : 'globalCurrency',
          displayName   : this.translateService.instant('GENERAL_SPACE.FIELD.BALANCE_DUE_LABEL'),
          enableCellEdit: false,
          field         : 'vendor_invoice_balance_due',
          width         : '150px'
        },
        {
          cellClass           : 'align-right',
          cellFilter          : 'globalCurrency',
          displayName         : this.translateService.instant('JS_SPACE.COLUMNS.APPLIED'),
          editableCellTemplate: EditableCurrencyCellHtml,
          field               : 'amount',
          width               : '115px'
        },
        {
          cellClass     : 'align-right ReadOnly',
          cellFilter    : 'globalCurrency',
          displayName   : this.translateService.instant('JS_SPACE.COLUMNS.BALANCE'),
          enableCellEdit: false,
          field         : 'getBalance()',
          width         : '115px'
        }
      ] };
  }

  getInvoicesForPayment ( vendor : VendorModel, params : TableQueryParameters = {}, isBranchPayment = false ) : Promise<Array<InvoiceModel>> {
    return isBranchPayment
      ? this.apiService.get('/vendors/branch_invoice_count', [
          vendor.id,
          'P',
          'I'
        ])
        .then(( count : number ) => this.apiService.get('/vendors/branch_invoice_list', [
          vendor.id,
          params.offset   || 0,
          params.pageSize || count,
          'P',
          'I',
          params.field     || 'post_date',
          params.direction || 'desc'
        ]))
      : this.apiService.get('/vendors/invoice_list', [
          vendor.id,
          params.offset   || 0,
          params.pageSize || '*',
          'P',
          'I',
          '*',
          '*',
          '*',
          params.field     || 'post_date',
          params.direction || 'desc'
        ]);
  }

  hasCredits ( vendor : VendorModel ) : boolean {


    const balance = new BigDecimal(vendor.credit_balance ? vendor.credit_balance.toString() : '0');


    return balance.isGreaterThan(BigDecimal.ZERO);


  }

  isOpen ( payment : VendorPaymentModel ) : boolean {


    return payment.status === 'O';


  }

  prepPayment ( vendor_id : string, payment : VendorPaymentModel ) : VendorPaymentModel {
    if (payment.id) {
      payment.itemsToDelete = [];

      payment.vendor_payment_items_attributes.forEach(( item : VendorPaymentItemModel ) => this.prepPaymentItem(payment, item));

      if (!payment.vendor_payment_items_attributes.length) {


        payment.vendor_payment_items_attributes.push(this.prepPaymentItem(payment));


      }

      return payment;
    }

    return {
      amount                         : '',
      applied_amount                 : '',
      id                             : '',
      itemsToDelete                  : [],
      note                           : '',
      post_date                      : this.dateParsing.today(),
      reference                      : '',
      status                         : 'O',
      vendor_id                      : vendor_id,
      vendor_payment_items_attributes: [ this.prepPaymentItem(payment) ],
      vendor_payment_number          : ''
    };
  }

  prepPaymentItem ( payment : VendorPaymentModel, item ?: VendorPaymentItemModel ) : VendorPaymentItemModel {
    item = item || {
      amount                    : '',
      acquired_type             : '',
      id                        : '',
      stock_number              : '',
      vendor_invoice_balance_due: '',
      vendor_invoice_id         : '',
      vendor_invoice_number     : '',
      vendor_invoice_post_date  : '',
      vendor_invoice_total      : '',
      vendor_name               : '',
      vendor_payment_id         : payment.id };

    item.untouched  = item.untouched !== undefined ? item.untouched : !item.id;
    item.getBalance = () => {
      const amount  = new BigDecimal( item.amount ? item.amount.toString(): '0' ).setScale(2, BigDecimal.ROUND_HALF_UP);
      const balance = new BigDecimal( item.vendor_invoice_balance_due ? item.vendor_invoice_balance_due.toString(): '0' ).setScale(2, BigDecimal.ROUND_HALF_UP);

      if (!this.isOpen(payment)) {
        return item.vendor_invoice_balance_due;
      }

      if (balance.isLessThanOrEqualTo(BigDecimal.ZERO)) {
        return 0;
      }

      return balance.subtract(amount).toString();
    };

    return item;
  }

  process ( payment : VendorPaymentModel, options = {} ) : Promise<VendorPaymentModel> {


    return this.confirmService.generic(this.translateService.instant('JS_SPACE.CONFIRM.SAVE_PROCESS_PAYMENT'))
    .then(() => this.save(payment))
    .then(( payment : VendorPaymentModel ) => this.apiService.get('/vendor_payments/process_payment', payment.id, null, options));


  }

  removeItem ( payment : VendorPaymentModel, index : number ) : VendorPaymentModel {
    const deletedItem = payment.vendor_payment_items_attributes.splice(index, 1)[0];

    if (payment.id && deletedItem.id) {


      deletedItem._destroy = '1';


      payment.itemsToDelete.push(deletedItem);


    }

    if (!payment.vendor_payment_items_attributes.length) {


      payment.vendor_payment_items_attributes = [ this.prepPaymentItem(payment) ];


    }

    return payment;
  }

  save ( payment : VendorPaymentModel ) : Promise<VendorPaymentModel> {


    const method = payment.id ? 'patch' : 'post';
    const url    = payment.id ? '/vendor_payment' : '/vendor_payments';

    return this.deleteItems(payment)
    .then(payment => this.apiService[method](url, payment, 'vendor_payment', 'vendor_payment_items_attributes'));


  }

  updateTotals ( payment : VendorPaymentModel ) : VendorPaymentModel {
    let total = BigDecimal.ZERO;

    payment.vendor_payment_items_attributes.forEach(item => {
      const amount = new BigDecimal( item.amount ? item.amount.toString(): '0' ).setScale(2, BigDecimal.ROUND_HALF_UP);

      total = total.add(amount);
    });

    payment.applied_amount = total ? total.toString() : '0';
    payment.amount         = payment.applied_amount;

    return payment;
  }

  void ( payment : VendorPaymentModel ) : Promise<boolean> {


    return this.confirmService.generic(this.translateService.instant('JS_SPACE.CONFIRM.VOID_PAYMENT'))
    .then(() => this.apiService.get('/vendor_payments/void_payment', payment.id));


  }
}
